import { computed, inject, Injectable, signal } from '@angular/core';
import { FormArray, FormBuilder, FormGroup, Validators } from '@angular/forms';
import {
  BOAT_TYPES,
  BUS_TYPES,
  CAR_TYPES,
  FlightStop,
  FlightStopType,
  PASSENGER_PER_VEHICLE,
  PER_PASSENGER_CAR_TYPES,
  PLANE_TYPES,
  PRIVATE_VEHICLE,
  RoadStop,
  RoadStopType,
  TRAIN_TYPES,
  Transport,
  TransportCategory,
  TransportMode,
  TWO_WHEELS_TYPES,
} from '../models/transport';
import { CalculationMode } from '../models/calculation-mode.enum';
import { TripService } from './trip.service';
import { addressValidator } from 'src/app/validators/address.validators';
import { SectionValidationService } from './section-validation.service';
import { ValidationIssue } from '../types/validation';
import { itineraryValidator } from 'src/app/validators/itinerary.validators';
import { debounceTime, distinctUntilChanged, Subscription } from 'rxjs';
import { FactorService } from 'src/app/factors/services/factor.service';
import { EmissionFactorCategory } from 'src/app/factors/types/factor';

@Injectable({
  providedIn: 'root',
})
export class TransportFormService {
  tripService = inject(TripService);
  fb = inject(FormBuilder);
  sectionValidationService = inject(SectionValidationService);
  factorService = inject(FactorService);
  step = signal<number>(1);
  mode = signal<'create' | 'edit'>('create');
  index = signal<number | null>(null);
  transportForm: FormGroup = this.createTransportGroup();
  transportCategory = signal<TransportCategory | null>(null);
  roadStopValueChangeSubscription?: Subscription;

  typeList = computed(() => {
    switch (this.transportCategory()) {
      case TransportCategory.Plane:
        return PLANE_TYPES;
      case TransportCategory.Train:
        return TRAIN_TYPES;
      case TransportCategory.Car:
        return CAR_TYPES;
      case TransportCategory.TwoWheels:
        return TWO_WHEELS_TYPES;
      case TransportCategory.Bus:
        return BUS_TYPES;
      case TransportCategory.Boat:
        return BOAT_TYPES;
      default:
        return [];
    }
  });
  calculationMode = signal<CalculationMode | null>(null);
  isCar = computed(() => {
    return this.transportCategory() === TransportCategory.Car;
  });

  customFactors = this.factorService.customFactors;
  transportFactor = computed(() => {
    return this.customFactors().filter(
      (factor) => factor.category === EmissionFactorCategory.Transport,
    );
  });

  get selectedCustomFactor() {
    const id = this.transportForm.get('customEmissionFactorId')?.value;
    if (!id) return null;
    return this.transportFactor().find((f) => f.id === id) ?? null;
  }

  createTransportGroup(): FormGroup {
    return this.fb.group({
      title: [''],
      type: ['', [Validators.required]],
      return: [true, [Validators.required]],
      isMainTransport: [false],
      scheduledDays: [[]],
    });
  }

  /**
   * Id of the transport currently being edited (saved transports only).
   * Undefined in create mode or for an unsaved transport — used to gate the
   * super-admin road-stop resolution debug feature.
   */
  get editedTransportId(): string | undefined {
    const i = this.index();
    if (this.mode() !== 'edit' || i === null) return undefined;
    return this.tripService.trip()?.transports[i]?.id;
  }

  public resetFormGroup() {
    this.transportForm = this.createTransportGroup();
    this.step.set(1);
    this.transportCategory.set(null);
    this.calculationMode.set(null);
    this.mode.set('create');
    this.index.set(null);
    this.roadStopValueChangeSubscription?.unsubscribe();
  }

  submitTransport() {
    const transport: Transport = this.transportForm.value;

    let result;
    if (this.mode() === 'edit' && this.index() !== null) {
      result = this.tripService.updateTransport(transport, this.index()!);
    } else {
      result = this.tripService.addTransport(transport);
    }

    // Validation will be triggered by trip service after save completes

    return result;
  }

  /*************************
   * AIRPORT FORM CONTROLS *
   *************************/
  createAirportControls() {
    if (!this.flightStops || this.flightStops.length < 1) {
      this.transportForm.addControl(
        'flightStops',
        this.fb.array([
          this.createFlightStop(0, FlightStopType.Departure),
          this.createFlightStop(1, FlightStopType.Arrival),
        ]),
      );
    }
  }
  destroyAirportControls() {
    this.transportForm.removeControl('flightStops');
  }
  /** Appends a stopover just before the arrival (used when loading an edit). */
  addAirport(): void {
    this.insertAirportAt(this.flightStops.length - 1);
  }
  /** Inserts a new stopover at `position`, then re-derives order/type from position. */
  insertAirportAt(position: number): void {
    if (!this.transportForm.contains('flightStops')) return;
    this.flightStops.insert(
      position,
      this.createFlightStop(position, FlightStopType.Stopover),
    );
    this.normalizeFlightStops();
  }
  removeAirport(index: number): void {
    if (!this.transportForm.contains('flightStops')) return;
    this.flightStops.removeAt(index);
    this.normalizeFlightStops();
  }
  /**
   * Moves the stop at `from` to position `to`.
   * `to` is interpreted as in the pre-removal list (same convention as
   * BatchUploadService.reorderFile), so dropping onto a later row places the
   * dragged stop just before it.
   */
  reorderFlightStop(from: number, to: number): void {
    if (!this.transportForm.contains('flightStops')) return;
    if (from < 0 || from >= this.flightStops.length || from === to) return;
    const control = this.flightStops.at(from);
    this.flightStops.removeAt(from);
    const target = from < to ? to - 1 : to;
    this.flightStops.insert(target, control);
    this.normalizeFlightStops();
  }
  /** Re-derives each stop's order and type from its position in the array. */
  private normalizeFlightStops(): void {
    const controls = this.flightStopsControls;
    const lastIndex = controls.length - 1;
    controls.forEach((control, index) => {
      const type =
        index === 0
          ? FlightStopType.Departure
          : index === lastIndex
            ? FlightStopType.Arrival
            : FlightStopType.Stopover;
      control.patchValue({ order: index, type }, { emitEvent: false });
    });
  }
  createFlightStop(order: number, type: FlightStopType) {
    return this.fb.group({
      airportCode: [null, Validators.required],
      order: [order, Validators.required],
      type: [type, Validators.required],
    });
  }
  get flightStops(): FormArray {
    return this.transportForm.get('flightStops') as FormArray;
  }
  get flightStopsControls(): FormGroup[] {
    return this.flightStops.controls as FormGroup[];
  }

  getFlightStopValue(index: number): string | undefined {
    if (!this.flightStops.at(index)) return undefined;
    return (
      (this.flightStops.at(index) as FormGroup).get('airportCode')?.value ||
      undefined
    );
  }
  onAirportSelected(airportCode: string, index: number) {
    (this.flightStops.at(index) as FormGroup).patchValue({
      airportCode: airportCode,
    });
  }

  /*********************
   * TIM FORM CONTROLS *
   *********************/
  createTimControls() {
    this.createAirportControls();
    this.transportForm.addControl(
      'carrierCode',
      this.fb.control('', [Validators.required]),
    );
    this.transportForm.addControl(
      'flightNumber',
      this.fb.control('', [Validators.required]),
    );
    this.transportForm.addControl(
      'flightDate',
      this.fb.control('', [Validators.required]),
    );
  }
  destroyTimControls() {
    this.destroyAirportControls();
    this.transportForm.removeControl('carrierCode');
    this.transportForm.removeControl('flightNumber');
    this.transportForm.removeControl('flightDate');
  }

  /**
   * ADDRESS FORM CONTROL
   */
  createAddressControls() {
    if (!this.transportForm.contains('roadStops')) {
      this.transportForm.addControl(
        'roadStops',
        this.fb.array(
          [
            this.createRoadStop(0, RoadStopType.Origin),
            this.createRoadStop(1, RoadStopType.Destination),
          ],
          { validators: itineraryValidator(this.issues) },
        ),
      );
      this.roadStopValueChangeSubscription = this.roadStops.valueChanges
        .pipe(debounceTime(300), distinctUntilChanged())
        .subscribe(() => {
          this.updateItineraryValidity();
        });
    }
  }
  destroyAddressControls() {
    this.transportForm.removeControl('roadStops');
    this.roadStopValueChangeSubscription?.unsubscribe();
  }
  get roadStopsControls() {
    return (this.transportForm.get('roadStops') as FormArray)
      .controls as FormGroup[];
  }
  get roadStops(): FormArray {
    return this.transportForm.get('roadStops') as FormArray;
  }
  /** Appends a waypoint just before the destination (used when loading an edit). */
  addWaypoint(): void {
    this.insertWaypointAt(this.roadStops.length - 1);
  }
  /** Inserts a new waypoint at `position`, then re-derives order/type from position. */
  insertWaypointAt(position: number): void {
    if (!this.transportForm.contains('roadStops')) return;
    this.roadStops.insert(
      position,
      this.createRoadStop(position, RoadStopType.Waypoint),
    );
    this.normalizeRoadStops();
  }
  removeWaypoint(index: number): void {
    if (!this.transportForm.contains('roadStops')) return;
    this.roadStops.removeAt(index);
    this.normalizeRoadStops();
  }
  /**
   * Moves the stop at `from` to position `to`.
   * `to` is interpreted as in the pre-removal list (same convention as
   * BatchUploadService.reorderFile), so dropping onto a later row places the
   * dragged stop just before it.
   */
  reorderRoadStop(from: number, to: number): void {
    if (!this.transportForm.contains('roadStops')) return;
    if (from < 0 || from >= this.roadStops.length || from === to) return;
    const control = this.roadStops.at(from);
    this.roadStops.removeAt(from);
    const target = from < to ? to - 1 : to;
    this.roadStops.insert(target, control);
    this.normalizeRoadStops();
  }
  /** Re-derives each stop's order and type from its position in the array. */
  private normalizeRoadStops(): void {
    const controls = this.roadStopsControls;
    const lastIndex = controls.length - 1;
    controls.forEach((control, index) => {
      const type =
        index === 0
          ? RoadStopType.Origin
          : index === lastIndex
            ? RoadStopType.Destination
            : RoadStopType.Waypoint;
      control.patchValue({ order: index, type }, { emitEvent: false });
    });
  }
  createRoadStop(order: number, type: RoadStopType) {
    return this.fb.group({
      address: [null, [Validators.required, addressValidator(this.issues)]],
      order: [order, Validators.required],
      type: [type, Validators.required],
    });
  }

  get issues() {
    let issues: ValidationIssue[] = [];
    const index = this.index();
    if (index !== null) {
      issues = this.sectionValidationService.getIssuesForItem(
        'transport',
        index,
      );
    }
    return issues;
  }

  updateItineraryValidity() {
    // 1. send updated info
    const index = this.index();
    const tripId = this.tripService.trip()?.id;
    if (index === null || !tripId) return;
    if (this.roadStopsControls.every((c) => c.valid)) {
      this.sectionValidationService
        .validateItinerary(this.roadStops.value, index, tripId)
        .subscribe({
          next: () => {
            this.roadStops.setValidators(itineraryValidator(this.issues));
            this.roadStops.updateValueAndValidity({ emitEvent: false });
          },
        });
    }
  }
  checkAllAddressValidity() {
    for (const roadStopControl of this.roadStopsControls) {
      if (roadStopControl.invalid) {
        return false;
      }
    }
    return true;
  }

  /**
   * DISTANCE FORM CONTROL
   */
  createDistanceControls() {
    if (!this.transportForm.contains('distance'))
      this.transportForm.addControl(
        'distance',
        this.fb.control('', [Validators.required, Validators.min(1)]),
      );
  }
  destroyDistanceControls() {
    this.transportForm.removeControl('distance');
  }

  /**
   * DURATION FORM CONTROL
   */

  createDurationControls() {
    if (!this.transportForm.contains('duration'))
      this.transportForm.addControl(
        'duration',
        this.fb.control('', [Validators.required, Validators.min(0)]),
      );
  }
  destroyDurationControls() {
    if (this.transportForm.contains('duration')) {
      this.transportForm.removeControl('duration');
    }
  }

  destroyAllCalculationModeControls() {
    this.destroyAirportControls();
    this.destroyTimControls();
    this.destroyAddressControls();
    this.destroyDistanceControls();
    this.destroyDurationControls();
  }
  resetCalculationMode() {
    this.destroyAllCalculationModeControls();
    this.calculationMode.set(null);
  }

  /************************
   * CONDITIONAL DISPLAY  *
   ************************/

  updateConditionnalDisplay() {
    this.displayVehicle
      ? this.createVehicleControl()
      : this.destroyVehicleControl();
    this.displayPassengerPerVehicle
      ? this.createPassengerPerVehicleControl()
      : this.destroyPassengerPerVehicleControl();
    this.displayPrivateVehicle
      ? this.createPrivateVehicleControl()
      : this.destroyPrivateVehicleControl();
    this.displaySubtype || this.transportCategory() === TransportCategory.Cruise
      ? this.createSubtypeControl()
      : this.destroySubtypeControl();
    this.transportCategory() === TransportCategory.Cruise
      ? this.createCruiseControls()
      : this.destroyCruiseControls();
    if (this.displayCustomEmissionFactors) {
      this.createCustomFactorControl();
    } else {
      this.destroyCustomFactorControl();
    }
  }

  get displayVehicle() {
    return this.transportForm.get('type')?.value === TransportMode.FerryWithCar;
  }
  createVehicleControl() {
    if (!this.transportForm.contains('vehicle')) {
      this.transportForm.addControl(
        'vehicle',
        this.fb.control(null, [Validators.min(0)]),
      );
    }
  }
  destroyVehicleControl() {
    if (this.transportForm.contains('vehicle')) {
      this.transportForm.removeControl('vehicle');
    }
  }
  get displayPassengerPerVehicle() {
    return PASSENGER_PER_VEHICLE.includes(
      this.transportForm.get('type')?.value,
    );
  }
  createPassengerPerVehicleControl() {
    if (!this.transportForm.contains('passengerPerVehicle')) {
      this.transportForm.addControl(
        'passengerPerVehicle',
        this.fb.control(null, [Validators.min(0), Validators.max(5)]),
      );
    }
  }
  destroyPassengerPerVehicleControl() {
    if (this.transportForm.contains('passengerPerVehicle')) {
      this.transportForm.removeControl('passengerPerVehicle');
    }
  }
  get displayPrivateVehicle() {
    return PRIVATE_VEHICLE.includes(this.transportForm.get('type')?.value);
  }
  createPrivateVehicleControl() {
    if (!this.transportForm.contains('isPrivate')) {
      this.transportForm.addControl('isPrivate', this.fb.control(false));
    }
    if (!this.transportForm.contains('numberOfVehicle')) {
      this.transportForm.addControl(
        'numberOfVehicle',
        this.fb.control(1, [Validators.required, Validators.min(1)]),
      );
    }
  }
  destroyPrivateVehicleControl() {
    if (this.transportForm.contains('isPrivate')) {
      this.transportForm.removeControl('isPrivate');
    }
    if (this.transportForm.contains('numberOfVehicle')) {
      this.transportForm.removeControl('numberOfVehicle');
    }
  }
  get isPrivate() {
    return this.transportForm.get('isPrivate')?.value;
  }

  get allowReturnOption() {
    return (
      this.transportForm.get('type')?.value !== TransportMode.Cruise &&
      this.calculationMode() !== CalculationMode.TIM
    );
  }

  /**
   * Gated on the type, not on the Car category: a taxi is a car with a single flat
   * factor, so it has no size to pick.
   */
  hasCarSize(type: TransportMode) {
    return CAR_TYPES.includes(type) && !PER_PASSENGER_CAR_TYPES.includes(type);
  }

  get displaySubtype() {
    return this.hasCarSize(this.transportForm.get('type')?.value);
  }
  createSubtypeControl() {
    if (!this.transportForm.contains('subtype')) {
      this.transportForm.addControl(
        'subtype',
        this.fb.control(null, [Validators.required]),
      );
    }
  }
  destroySubtypeControl() {
    if (this.transportForm.contains('subtype')) {
      this.transportForm.removeControl('subtype');
    }
  }

  createCruiseControls() {
    if (!this.transportForm.contains('duration')) {
      this.transportForm.addControl(
        'duration',
        this.fb.control(null, [Validators.required]),
      );
    }
    if (!this.transportForm.contains('daysWithoutTravel')) {
      this.transportForm.addControl('daysWithoutTravel', this.fb.control(null));
    }
    if (!this.transportForm.contains('distancePerDay')) {
      this.transportForm.addControl('distancePerDay', this.fb.control(null));
    }
  }
  destroyCruiseControls() {
    if (
      this.transportForm.contains('duration') &&
      this.calculationMode() !== CalculationMode.Duration
    ) {
      this.transportForm.removeControl('duration');
    }
    if (this.transportForm.contains('daysWithoutTravel')) {
      this.transportForm.removeControl('daysWithoutTravel');
    }
    if (this.transportForm.contains('distancePerDay')) {
      this.transportForm.removeControl('distancePerDay');
    }
  }

  get displayCustomEmissionFactors() {
    const type = this.transportForm.get('type')?.value;
    return type === TransportMode.CustomFactor;
  }

  createCustomFactorControl() {
    if (!this.transportForm.contains('customEmissionFactorId')) {
      this.transportForm.addControl(
        'customEmissionFactorId',
        this.fb.control('', [Validators.required]),
      );
    }
  }
  destroyCustomFactorControl() {
    if (this.transportForm.contains('customEmissionFactorId')) {
      this.transportForm.removeControl('customEmissionFactorId');
    }
  }

  /************
   * POPULATE *
   ************/
  editTransport(index: number) {
    this.mode.set('edit');
    this.index.set(index);
    const transport = this.tripService.trip()?.transports[index];
    if (!transport) {
      console.error('could not load transport');
      return;
    }
    this.transportCategory.set(this.getCategoryFromType(transport.type));
    this.calculationMode.set(this.getCalculationMode(transport));
    this.addConditionnalControls(transport);
    // Reset the form with values instead of patchValue to avoid "phantom values"
    this.transportForm.reset(transport);
  }

  getCategoryFromType(type: TransportMode) {
    switch (true) {
      case PLANE_TYPES.includes(type):
        return TransportCategory.Plane;
      case TRAIN_TYPES.includes(type):
        return TransportCategory.Train;
      case CAR_TYPES.includes(type):
        return TransportCategory.Car;
      case TWO_WHEELS_TYPES.includes(type):
        return TransportCategory.TwoWheels;
      case BUS_TYPES.includes(type):
        return TransportCategory.Bus;
      case BOAT_TYPES.includes(type):
        return TransportCategory.Boat;
      case type === TransportMode.Cruise:
        return TransportCategory.Cruise;
      case type === TransportMode.CustomFactor:
        return TransportCategory.CustomFactor;
      default:
        return null;
    }
  }
  getCalculationMode(transport: Transport) {
    switch (true) {
      case transport.type === TransportMode.Cruise:
        return null;
      case this.hasFlightDetails(transport):
        return CalculationMode.TIM;
      case this.hasExistingAirport(transport):
        return CalculationMode.Airport;
      case this.hasExistingAddress(transport):
        return CalculationMode.Address;
      case transport.distance && transport.distance > 0:
        return CalculationMode.Distance;
      case transport.duration && transport.duration > 0:
        return CalculationMode.Duration;
      default:
        return null;
    }
  }
  private hasExistingAddress(transport: Transport): boolean {
    return Boolean(
      transport.roadStops &&
        transport.roadStops.length > 0 &&
        transport.roadStops.some(
          (stop: { address: string; type: RoadStopType }) => stop.address,
        ),
    );
  }
  private hasExistingAirport(transport: Transport): boolean {
    return Boolean(
      transport.flightStops &&
        transport.flightStops.length > 0 &&
        transport.flightStops.some(
          (stop: { airportCode: string; type: FlightStopType }) =>
            stop.airportCode,
        ),
    );
  }
  private hasFlightDetails(transport: Transport): boolean {
    const hasFlightDetails =
      Boolean(transport.flightNumber) &&
      Boolean(transport.flightDate) &&
      Boolean(transport.carrierCode);
    return hasFlightDetails;
  }
  addConditionnalControls(transport: Transport) {
    if (transport.type === TransportMode.CustomFactor) {
      this.createCustomFactorControl();
    }
    switch (this.calculationMode()) {
      case CalculationMode.Airport:
        this.createFlightStopsControls(transport.flightStops!);
        break;
      case CalculationMode.TIM:
        this.createTimControls();
        break;
      case CalculationMode.Address:
        this.createRoadStopsControls(transport.roadStops!);
        break;
      case CalculationMode.Distance:
        this.createDistanceControls();
        break;
      case CalculationMode.Duration || transport.type === TransportMode.Cruise:
        this.createDurationControls();
        break;
    }

    transport.type === TransportMode.FerryWithCar &&
      this.createVehicleControl();
    PASSENGER_PER_VEHICLE.includes(transport.type) &&
      this.createPassengerPerVehicleControl();
    PRIVATE_VEHICLE.includes(transport.type) &&
      this.createPrivateVehicleControl();
    // Reads the transport, not the form: this runs before transportForm.reset().
    (this.hasCarSize(transport.type) ||
      this.transportCategory() === TransportCategory.Cruise) &&
      this.createSubtypeControl();
    this.transportCategory() === TransportCategory.Cruise &&
      this.createCruiseControls();
  }
  createFlightStopsControls(flightStops: FlightStop[]) {
    this.createAirportControls();
    flightStops.forEach((flightstop) => {
      flightstop.type === FlightStopType.Stopover && this.addAirport();
    });
  }
  createRoadStopsControls(roadStops: RoadStop[]) {
    this.createAddressControls();
    roadStops.forEach((roadStop) => {
      roadStop.type === RoadStopType.Waypoint && this.addWaypoint();
    });
  }
}
